import type { ReactNode } from 'react';
import ModalShell from './ModalShell';
import Button from './Button';
import { Icon } from './Icon';

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  /** Plain string, or rich inline content (e.g. multi-line with an emphasised span). */
  message: ReactNode;
  /** Label for the confirm button. Default: "Confirmar" */
  confirmLabel?: string;
  /** Controls confirm button colour. Default: "danger" for destructive actions; pass "primary" for safe ones like restore. */
  variant?: 'primary' | 'danger' | 'warning';
  /** Shows a spinner and disables the confirm button while an async action is in progress. */
  loading?: boolean;
  /**
   * Stacking tier. Defaults to `z-[10000]` — above every popover/dropdown in
   * the app (DropdownSelect z-[200], date/color picker portals z-[9999]). A
   * confirmation is a blocking interrupt, so it must sit on top of any control
   * the user left open (e.g. an open dropdown when the unsaved-changes guard
   * fires). Override only if you deliberately need it lower.
   */
  zIndex?: string;
}

/**
 * Accessible, styled confirmation dialog — replaces all `window.confirm()` calls.
 *
 * Standard component for ALL destructive or irreversible actions across admin and tenant.
 * Import from `@shared` — never use `window.confirm()` or `confirm()`.
 *
 * @example
 * <ConfirmDialog
 *   isOpen={confirmOpen}
 *   onClose={() => setConfirmOpen(false)}
 *   onConfirm={handleDelete}
 *   title="Eliminar pedido"
 *   message="Esta acción no se puede deshacer. ¿Continuar?"
 * />
 */
export default function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirmar',
  variant = 'danger',
  loading = false,
  zIndex = 'z-[10000]',
}: ConfirmDialogProps) {
  const buttonVariant = variant === 'danger' ? 'danger' : 'primary';
  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      maxWidth="max-w-md"
      size="auto"
      zIndex={zIndex}
      headerActions={
        <Button
          variant={buttonVariant}
          onClick={onConfirm}
          disabled={loading}
          data-testid="confirm-dialog-confirm"
        >
          {loading ? (
            <Icon name="progress_activity" className="!text-[18px] animate-spin" />
          ) : null}
          {confirmLabel}
        </Button>
      }
    >
      <p className="text-sm text-text-main dark:text-slate-300 leading-relaxed">{message}</p>
    </ModalShell>
  );
}
